import { usePage, Head } from '@inertiajs/react';
import React, { useEffect, useState } from 'react';
import ProfileLink from '@/Components/Surfaces/ProfileLink';
import AppTextLogo from '@/Components/Utility/AppTextLogo';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import FormSignUp from '@/Blocks/Forms/FormSignUp';

export default function LinksProfile({ profile }) {
  const page = usePage();
  const [pageView, setPageView] = useState('links');
  const [treeCount, setTreeCount] = useState(null);

  const categories = page.props.categories;
  const profileLinks = profile.profile_links;

  const inviteeFirstName =
    decodeURIComponent(getAllUrlParams().invitee_full_name).split(' ')[0] ||
    null;
  const inviteeFullName =
    decodeURIComponent(getAllUrlParams().invitee_full_name) || null;
  const inviteeEmail =
    decodeURIComponent(getAllUrlParams().invitee_email) || null;
  const eventTypeName =
    decodeURIComponent(getAllUrlParams().event_type_name) || null;
  const eventStartTime =
    decodeURIComponent(getAllUrlParams().event_start_time) || null;
  const eventType = eventTypeName.includes('coffee')
    ? 'coffee'
    : eventTypeName.includes('meeting')
    ? 'meeting'
    : eventTypeName.includes('discovery')
    ? 'discovery session'
    : eventTypeName.includes('audit')
    ? 'audit'
    : 'chat';
  const inviteeEmailCheck = inviteeEmail.includes('undefined')
    ? ''
    : inviteeEmail;

  const splitInviteeFullName = inviteeFullName.split(' ');
  const capitalisedSplitInviteeFullName = splitInviteeFullName
    .map(fullName => {
      return fullName[0].toUpperCase() + fullName.substring(1);
    })
    .join(' ');

  const inviteeFullNameCheck = inviteeEmail.includes('undefined')
    ? ''
    : capitalisedSplitInviteeFullName;

  useEffect(() => {
    fetch('https://public.ecologi.com/users/madebystudio/impact')
      .then(response => response.json())
      .then(data => setTreeCount(data.trees));
  }, []);

  const animateLinkView = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5, delay: 0.6 } },
    exit: { opacity: 0, transition: { duration: 0.3, delay: 0.6 } },
  };

  const animateQRView = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5, delay: 0.6 } },
    exit: { opacity: 0, transition: { duration: 1, delay: 0.6 } },
  };

  const handleViewChange = view => {
    window.scrollTo(0, 0);
    setPageView(view);
  };

  function getAllUrlParams(url) {
    // get query string from url (optional) or window
    var queryString = url ? url.split('?')[1] : window.location.search.slice(1);

    // we'll store the parameters here
    var obj = {};

    // if query string exists
    if (queryString) {
      // stuff after # is not part of query string, so get rid of it
      queryString = queryString.split('#')[0];

      // split our query string into its component parts
      var arr = queryString.split('&');

      for (var i = 0; i < arr.length; i++) {
        // separate the keys and the values
        var a = arr[i].split('=');

        // set parameter name and value (use 'true' if empty)
        var paramName = a[0];
        var paramValue = typeof a[1] === 'undefined' ? true : a[1];

        // (optional) keep case consistent
        paramName = paramName.toLowerCase();
        if (typeof paramValue === 'string')
          paramValue = paramValue.toLowerCase();

        // if the paramName ends with square brackets, e.g. colors[] or colors[2]
        if (paramName.match(/\[(\d+)?\]$/)) {
          // create key if it doesn't exist
          var key = paramName.replace(/\[(\d+)?\]/, '');
          if (!obj[key]) obj[key] = [];

          // if it's an indexed array e.g. colors[2]
          if (paramName.match(/\[\d+\]$/)) {
            // get the index value and add the entry at the appropriate position
            var index = /\[(\d+)\]/.exec(paramName)[1];
            obj[key][index] = paramValue;
          } else {
            // otherwise add the value to the end of the array
            obj[key].push(paramValue);
          }
        } else {
          // we're dealing with a string
          if (!obj[paramName]) {
            // if it doesn't exist, create property
            obj[paramName] = paramValue;
          } else if (obj[paramName] && typeof obj[paramName] === 'string') {
            // if property does exist and it's a string, convert it to an array
            obj[paramName] = [obj[paramName]];
            obj[paramName].push(paramValue);
          } else {
            // otherwise add the property
            obj[paramName].push(paramValue);
          }
        }
      }
    }

    return obj;
  }

  return (
    <div className="relative flex">
      <Head>
        <title>{profile.name}</title>
        <meta
          name="description"
          content={`${profile.name} Links - Transform your digital performance`}
        />
        <meta
          name="keywords"
          content="graphic design, web developer, developer, leeds design, leeds developer, leeds laravel, leeds vue, design studio, design studio leeds"
        />
        <meta property="og:url" content={window.location.href} />
        <link rel="alternate" href="https://madeby.studio" hreflang="en-GB" />
        <meta property="og:site_name" content="madeby.studio - Profiles" />
        <meta
          name="twitter:title"
          content={`${profile.name} | madeby.studio`}
        />
        <meta
          name="twitter:description"
          content={`${profile.name}'s Links - Transform your digital performance`}
        />

        <meta property="og:title" content={`${profile.name} | madeby.studio`} />
        <meta
          property="og:description"
          content={`${profile.name}'s Links - Transform your digital performance`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en-GB" />
        <meta
          property="og:image"
          content="https://madeby.studio/assets/branding/meta-opengraph.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="madeby.studio - Team Working and Strapline"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://madeby.studio/assets/branding/twitter-opengraph.png"
        />
        <meta name="twitter:site" content="@madebystudio" />
        <meta name="twitter:creator" content="@madebystudio" />
      </Head>

      <div className="mt-12 mb-8 w-screen 2xl:w-6/12">
        <div className="mx-auto flex max-w-screen-sm flex-col items-center px-4">
          {inviteeFirstName != 'undefined' && (
            <div className="flex max-w-xs flex-col items-center text-center">
              <p className="text-xl font-medium capitalize text-dark mb-2">
                Thanks {inviteeFirstName}!
              </p>
              {eventTypeName != 'audit' ? (
                <>
                    <p className="text-base text-dark">
                        We look forward to our {eventType} on <br />{' '}
                        {format(new Date(eventStartTime), 'dd/MM/yyyy @ hh:mma')}
                    </p>
                    <p className="mt-4 mb-6 text-base text-dark">
                        In the meantime, feel free to take a look at our links or
                        sign-up for our newsletter. &#128071;
                    </p>
                </>
              ) : (
                <>
                    <p className="text-base text-dark">
                        We have recieved your request for a free website sustainability {eventType}.<br />{' '}
                    </p>
                    <p className="mt-4 mb-6 text-base text-dark">
                        In the meantime, feel free to take a look at our links or
                        sign-up for our newsletter. &#128071;
                    </p>
                </>
              )}
            </div>
          )}
          {inviteeEmail != 'undefined' && (
            <FormSignUp
              inviteeEmail={inviteeEmailCheck}
              inviteeName={inviteeFullNameCheck}
            />
          )}
          <div className="mb-6 h-40 w-40 overflow-hidden  rounded-full">
            <img
              className="aspect-square h-auto w-full object-cover"
              src={profile.profile_photo_url}
              width="600"
              height="600"
              alt={
                `${profile.name}` -
                `${profile.user_role}` +
                'Headshot' +
                '| madeby.studio'
              }
            />
          </div>
          <h1 className="w-fit text-3xl">
            {profile.name}
            <span className="text-primary">.</span>
          </h1>
          <p className="text-lg">
            {profile.user_role && profile.user_role + ' ' + '//'} madeby.studio
          </p>
          <ul className="my-4 flex flex-wrap justify-center gap-x-2 gap-y-2">
            {profile.phone ? (
              <li>
                <a
                  href={`tel:${profile.phone}`}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="black" />
                    <path
                      d="M28.6396 22.2876C28.4321 22.2876 28.2153 22.2216 28.0078 22.1745C27.5878 22.0819 27.175 21.959 26.7727 21.8068C26.3353 21.6477 25.8545 21.6559 25.4229 21.83C24.9912 22.004 24.6391 22.3316 24.4345 22.7496L24.227 23.1739C23.3087 22.6631 22.4648 22.0286 21.7191 21.2882C20.9787 20.5424 20.3442 19.6986 19.8334 18.7802L20.2294 18.5163C20.6474 18.3116 20.975 17.9595 21.149 17.5279C21.3231 17.0962 21.3313 16.6154 21.1722 16.178C21.0225 15.7749 20.8997 15.3622 20.8045 14.9429C20.7574 14.7354 20.7197 14.5186 20.6914 14.3017C20.5769 13.6376 20.229 13.0362 19.7104 12.6058C19.1919 12.1754 18.5367 11.9443 17.8628 11.954H15.0343C14.628 11.9502 14.2256 12.034 13.8545 12.1997C13.4835 12.3654 13.1525 12.609 12.8841 12.9141C12.6157 13.2192 12.4161 13.5786 12.2991 13.9677C12.182 14.3568 12.1502 14.7666 12.2058 15.1692C12.708 19.1191 14.512 22.7891 17.3326 25.5995C20.1533 28.4099 23.8298 30.2005 27.7816 30.6884H28.1398C28.8351 30.6894 29.5064 30.4343 30.0255 29.9718C30.3238 29.705 30.5621 29.378 30.7246 29.0123C30.8871 28.6466 30.9702 28.2506 30.9684 27.8504V25.0219C30.9568 24.367 30.7183 23.7364 30.2936 23.2377C29.8689 22.739 29.2843 22.4033 28.6396 22.2876ZM29.111 27.9447C29.1108 28.0786 29.0821 28.2109 29.0269 28.3328C28.9716 28.4547 28.891 28.5635 28.7904 28.6518C28.6851 28.7428 28.5619 28.8107 28.4287 28.8513C28.2956 28.8919 28.1555 28.9043 28.0173 28.8876C24.4862 28.4348 21.2064 26.8194 18.6951 24.2962C16.1838 21.7729 14.584 18.4855 14.148 14.9523C14.133 14.8142 14.1462 14.6745 14.1867 14.5416C14.2272 14.4087 14.2942 14.2854 14.3837 14.1792C14.4721 14.0786 14.5808 13.998 14.7028 13.9427C14.8247 13.8874 14.957 13.8588 15.0909 13.8586H17.9194C18.1387 13.8537 18.3528 13.9254 18.5248 14.0614C18.6969 14.1973 18.8162 14.389 18.8622 14.6034C18.9 14.8612 18.9471 15.1157 19.0037 15.3671C19.1126 15.8642 19.2575 16.3526 19.4374 16.8286L18.1174 17.4414C18.0045 17.4932 17.903 17.5668 17.8187 17.6579C17.7343 17.749 17.6688 17.8559 17.6259 17.9724C17.5829 18.0889 17.5634 18.2128 17.5685 18.3368C17.5736 18.4609 17.6031 18.5828 17.6554 18.6954C19.0123 21.602 21.3488 23.9384 24.2553 25.2953C24.4849 25.3896 24.7423 25.3896 24.9719 25.2953C25.0895 25.2533 25.1975 25.1883 25.2898 25.1041C25.382 25.0199 25.4567 24.9183 25.5093 24.805L26.0939 23.485C26.5813 23.6594 27.0787 23.8042 27.5836 23.9188C27.835 23.9753 28.0896 24.0225 28.3473 24.0602C28.5617 24.1062 28.7534 24.2255 28.8893 24.3976C29.0253 24.5697 29.097 24.7838 29.0921 25.003L29.111 27.9447Z"
                      fill="#00EB91"
                    />
                  </svg>
                  <span className="sr-only">Call {profile.phone}</span>
                </a>
              </li>
            ) : (
              <li>
                <a
                  href="tel:+44 (0) 113 347 7734"
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="black" />
                    <path
                      d="M28.6396 22.2876C28.4321 22.2876 28.2153 22.2216 28.0078 22.1745C27.5878 22.0819 27.175 21.959 26.7727 21.8068C26.3353 21.6477 25.8545 21.6559 25.4229 21.83C24.9912 22.004 24.6391 22.3316 24.4345 22.7496L24.227 23.1739C23.3087 22.6631 22.4648 22.0286 21.7191 21.2882C20.9787 20.5424 20.3442 19.6986 19.8334 18.7802L20.2294 18.5163C20.6474 18.3116 20.975 17.9595 21.149 17.5279C21.3231 17.0962 21.3313 16.6154 21.1722 16.178C21.0225 15.7749 20.8997 15.3622 20.8045 14.9429C20.7574 14.7354 20.7197 14.5186 20.6914 14.3017C20.5769 13.6376 20.229 13.0362 19.7104 12.6058C19.1919 12.1754 18.5367 11.9443 17.8628 11.954H15.0343C14.628 11.9502 14.2256 12.034 13.8545 12.1997C13.4835 12.3654 13.1525 12.609 12.8841 12.9141C12.6157 13.2192 12.4161 13.5786 12.2991 13.9677C12.182 14.3568 12.1502 14.7666 12.2058 15.1692C12.708 19.1191 14.512 22.7891 17.3326 25.5995C20.1533 28.4099 23.8298 30.2005 27.7816 30.6884H28.1398C28.8351 30.6894 29.5064 30.4343 30.0255 29.9718C30.3238 29.705 30.5621 29.378 30.7246 29.0123C30.8871 28.6466 30.9702 28.2506 30.9684 27.8504V25.0219C30.9568 24.367 30.7183 23.7364 30.2936 23.2377C29.8689 22.739 29.2843 22.4033 28.6396 22.2876ZM29.111 27.9447C29.1108 28.0786 29.0821 28.2109 29.0269 28.3328C28.9716 28.4547 28.891 28.5635 28.7904 28.6518C28.6851 28.7428 28.5619 28.8107 28.4287 28.8513C28.2956 28.8919 28.1555 28.9043 28.0173 28.8876C24.4862 28.4348 21.2064 26.8194 18.6951 24.2962C16.1838 21.7729 14.584 18.4855 14.148 14.9523C14.133 14.8142 14.1462 14.6745 14.1867 14.5416C14.2272 14.4087 14.2942 14.2854 14.3837 14.1792C14.4721 14.0786 14.5808 13.998 14.7028 13.9427C14.8247 13.8874 14.957 13.8588 15.0909 13.8586H17.9194C18.1387 13.8537 18.3528 13.9254 18.5248 14.0614C18.6969 14.1973 18.8162 14.389 18.8622 14.6034C18.9 14.8612 18.9471 15.1157 19.0037 15.3671C19.1126 15.8642 19.2575 16.3526 19.4374 16.8286L18.1174 17.4414C18.0045 17.4932 17.903 17.5668 17.8187 17.6579C17.7343 17.749 17.6688 17.8559 17.6259 17.9724C17.5829 18.0889 17.5634 18.2128 17.5685 18.3368C17.5736 18.4609 17.6031 18.5828 17.6554 18.6954C19.0123 21.602 21.3488 23.9384 24.2553 25.2953C24.4849 25.3896 24.7423 25.3896 24.9719 25.2953C25.0895 25.2533 25.1975 25.1883 25.2898 25.1041C25.382 25.0199 25.4567 24.9183 25.5093 24.805L26.0939 23.485C26.5813 23.6594 27.0787 23.8042 27.5836 23.9188C27.835 23.9753 28.0896 24.0225 28.3473 24.0602C28.5617 24.1062 28.7534 24.2255 28.8893 24.3976C29.0253 24.5697 29.097 24.7838 29.0921 25.003L29.111 27.9447Z"
                      fill="#00EB91"
                    />
                  </svg>
                  <span className="sr-only">Call madeby.studio</span>
                </a>
              </li>
            )}
            {profile.contact_email ? (
              <li>
                <a
                  href={`mailto:${profile.contact_email}`}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="43"
                    viewBox="0 0 43 43"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21.462" r="21" fill="black" />
                    <path
                      d="M28.7137 13.291H15.223C14.4563 13.291 13.721 13.5956 13.1789 14.1377C12.6367 14.6799 12.3322 15.4152 12.3322 16.1819V25.8181C12.3322 26.5848 12.6367 27.3201 13.1789 27.8622C13.721 28.4044 14.4563 28.709 15.223 28.709H28.7137C29.4804 28.709 30.2157 28.4044 30.7579 27.8622C31.3 27.3201 31.6046 26.5848 31.6046 25.8181V16.1819C31.6046 15.4152 31.3 14.6799 30.7579 14.1377C30.2157 13.5956 29.4804 13.291 28.7137 13.291ZM28.3186 15.2183L22.6525 20.8844C22.563 20.9747 22.4564 21.0464 22.3389 21.0953C22.2215 21.1442 22.0956 21.1694 21.9684 21.1694C21.8412 21.1694 21.7152 21.1442 21.5978 21.0953C21.4803 21.0464 21.3738 20.9747 21.2842 20.8844L15.6181 15.2183H28.3186ZM29.6773 25.8181C29.6773 26.0737 29.5758 26.3188 29.3951 26.4995C29.2144 26.6802 28.9693 26.7817 28.7137 26.7817H15.223C14.9674 26.7817 14.7223 26.6802 14.5416 26.4995C14.3609 26.3188 14.2594 26.0737 14.2594 25.8181V16.577L19.9255 22.2431C20.4675 22.7844 21.2023 23.0885 21.9684 23.0885C22.7344 23.0885 23.4692 22.7844 24.0112 22.2431L29.6773 16.577V25.8181Z"
                      fill="#00EB91"
                    />
                  </svg>
                  <span className="sr-only">Email {profile.contact_email}</span>
                </a>
              </li>
            ) : (
              <li>
                <a
                  href="mailto:hello@madeby.studio"
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="43"
                    viewBox="0 0 43 43"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21.462" r="21" fill="black" />
                    <path
                      d="M28.7137 13.291H15.223C14.4563 13.291 13.721 13.5956 13.1789 14.1377C12.6367 14.6799 12.3322 15.4152 12.3322 16.1819V25.8181C12.3322 26.5848 12.6367 27.3201 13.1789 27.8622C13.721 28.4044 14.4563 28.709 15.223 28.709H28.7137C29.4804 28.709 30.2157 28.4044 30.7579 27.8622C31.3 27.3201 31.6046 26.5848 31.6046 25.8181V16.1819C31.6046 15.4152 31.3 14.6799 30.7579 14.1377C30.2157 13.5956 29.4804 13.291 28.7137 13.291ZM28.3186 15.2183L22.6525 20.8844C22.563 20.9747 22.4564 21.0464 22.3389 21.0953C22.2215 21.1442 22.0956 21.1694 21.9684 21.1694C21.8412 21.1694 21.7152 21.1442 21.5978 21.0953C21.4803 21.0464 21.3738 20.9747 21.2842 20.8844L15.6181 15.2183H28.3186ZM29.6773 25.8181C29.6773 26.0737 29.5758 26.3188 29.3951 26.4995C29.2144 26.6802 28.9693 26.7817 28.7137 26.7817H15.223C14.9674 26.7817 14.7223 26.6802 14.5416 26.4995C14.3609 26.3188 14.2594 26.0737 14.2594 25.8181V16.577L19.9255 22.2431C20.4675 22.7844 21.2023 23.0885 21.9684 23.0885C22.7344 23.0885 23.4692 22.7844 24.0112 22.2431L29.6773 16.577V25.8181Z"
                      fill="#00EB91"
                    />
                  </svg>
                  <span className="sr-only">Email madeby.studio</span>
                </a>
              </li>
            )}
            {profile.linkedin_url ? (
              <li>
                <a
                  href={profile.linkedin_url}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="#2F66BC" />
                    <path
                      d="M29.5511 12.0476H14.3857C14.2152 12.0452 14.046 12.0764 13.8876 12.1395C13.7292 12.2026 13.5847 12.2962 13.4625 12.4151C13.3403 12.534 13.2427 12.6758 13.1753 12.8324C13.1079 12.989 13.072 13.1573 13.0697 13.3278V28.6722C13.072 28.8427 13.1079 29.0111 13.1753 29.1677C13.2427 29.3243 13.3403 29.466 13.4625 29.5849C13.5847 29.7038 13.7292 29.7974 13.8876 29.8605C14.046 29.9236 14.2152 29.9548 14.3857 29.9524H29.5511C29.7216 29.9548 29.8909 29.9236 30.0493 29.8605C30.2077 29.7974 30.3521 29.7038 30.4743 29.5849C30.5965 29.466 30.6941 29.3243 30.7615 29.1677C30.8289 29.0111 30.8648 28.8427 30.8671 28.6722V13.3278C30.8648 13.1573 30.8289 12.989 30.7615 12.8324C30.6941 12.6758 30.5965 12.534 30.4743 12.4151C30.3521 12.2962 30.2077 12.2026 30.0493 12.1395C29.8909 12.0764 29.7216 12.0452 29.5511 12.0476ZM18.468 27.0339H15.7823V18.9768H18.468V27.0339ZM17.1252 17.8488C16.7548 17.8488 16.3995 17.7016 16.1376 17.4397C15.8757 17.1778 15.7286 16.8226 15.7286 16.4522C15.7286 16.0818 15.8757 15.7266 16.1376 15.4646C16.3995 15.2027 16.7548 15.0556 17.1252 15.0556C17.3218 15.0333 17.521 15.0528 17.7096 15.1128C17.8983 15.1728 18.0721 15.272 18.2197 15.4038C18.3674 15.5357 18.4855 15.6972 18.5664 15.8779C18.6473 16.0585 18.6891 16.2542 18.6891 16.4522C18.6891 16.6501 18.6473 16.8458 18.5664 17.0265C18.4855 17.2071 18.3674 17.3687 18.2197 17.5005C18.0721 17.6324 17.8983 17.7316 17.7096 17.7916C17.521 17.8516 17.3218 17.8711 17.1252 17.8488ZM28.1545 27.0339H25.4688V22.7099C25.4688 21.6267 25.0839 20.9194 24.108 20.9194C23.806 20.9216 23.512 21.0164 23.2655 21.1908C23.019 21.3653 22.8319 21.6112 22.7294 21.8952C22.6593 22.1057 22.629 22.3273 22.6398 22.5488V27.025H19.9541C19.9541 27.025 19.9541 19.7019 19.9541 18.9678H22.6398V20.1048C22.8838 19.6814 23.2387 19.3326 23.6662 19.096C24.0937 18.8594 24.5777 18.7439 25.066 18.7619C26.8564 18.7619 28.1545 19.9168 28.1545 22.3966V27.0339Z"
                      fill="white"
                    />
                  </svg>
                  <span className="sr-only">{profile.name} LinkedIn</span>
                </a>
              </li>
            ) : (
              <li>
                <a
                  href="https://www.linkedin.com/company/made-by-studio/"
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="#2F66BC" />
                    <path
                      d="M29.5511 12.0476H14.3857C14.2152 12.0452 14.046 12.0764 13.8876 12.1395C13.7292 12.2026 13.5847 12.2962 13.4625 12.4151C13.3403 12.534 13.2427 12.6758 13.1753 12.8324C13.1079 12.989 13.072 13.1573 13.0697 13.3278V28.6722C13.072 28.8427 13.1079 29.0111 13.1753 29.1677C13.2427 29.3243 13.3403 29.466 13.4625 29.5849C13.5847 29.7038 13.7292 29.7974 13.8876 29.8605C14.046 29.9236 14.2152 29.9548 14.3857 29.9524H29.5511C29.7216 29.9548 29.8909 29.9236 30.0493 29.8605C30.2077 29.7974 30.3521 29.7038 30.4743 29.5849C30.5965 29.466 30.6941 29.3243 30.7615 29.1677C30.8289 29.0111 30.8648 28.8427 30.8671 28.6722V13.3278C30.8648 13.1573 30.8289 12.989 30.7615 12.8324C30.6941 12.6758 30.5965 12.534 30.4743 12.4151C30.3521 12.2962 30.2077 12.2026 30.0493 12.1395C29.8909 12.0764 29.7216 12.0452 29.5511 12.0476ZM18.468 27.0339H15.7823V18.9768H18.468V27.0339ZM17.1252 17.8488C16.7548 17.8488 16.3995 17.7016 16.1376 17.4397C15.8757 17.1778 15.7286 16.8226 15.7286 16.4522C15.7286 16.0818 15.8757 15.7266 16.1376 15.4646C16.3995 15.2027 16.7548 15.0556 17.1252 15.0556C17.3218 15.0333 17.521 15.0528 17.7096 15.1128C17.8983 15.1728 18.0721 15.272 18.2197 15.4038C18.3674 15.5357 18.4855 15.6972 18.5664 15.8779C18.6473 16.0585 18.6891 16.2542 18.6891 16.4522C18.6891 16.6501 18.6473 16.8458 18.5664 17.0265C18.4855 17.2071 18.3674 17.3687 18.2197 17.5005C18.0721 17.6324 17.8983 17.7316 17.7096 17.7916C17.521 17.8516 17.3218 17.8711 17.1252 17.8488ZM28.1545 27.0339H25.4688V22.7099C25.4688 21.6267 25.0839 20.9194 24.108 20.9194C23.806 20.9216 23.512 21.0164 23.2655 21.1908C23.019 21.3653 22.8319 21.6112 22.7294 21.8952C22.6593 22.1057 22.629 22.3273 22.6398 22.5488V27.025H19.9541C19.9541 27.025 19.9541 19.7019 19.9541 18.9678H22.6398V20.1048C22.8838 19.6814 23.2387 19.3326 23.6662 19.096C24.0937 18.8594 24.5777 18.7439 25.066 18.7619C26.8564 18.7619 28.1545 19.9168 28.1545 22.3966V27.0339Z"
                      fill="white"
                    />
                  </svg>
                  <span className="sr-only">madeby.studio LinkedIn</span>
                </a>
              </li>
            )}
            {profile.twitter_url ? (
              <li>
                <a
                  href={profile.twitter_url}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="#00ACEE" />
                    <path
                      d="M32.3055 15.2773C31.559 15.6083 30.7672 15.8255 29.9563 15.9216C30.8105 15.4106 31.4496 14.6064 31.7547 13.6589C30.9519 14.1358 30.0732 14.4715 29.1569 14.6515C28.5955 14.0536 27.8675 13.6382 27.0672 13.4591C26.2669 13.2801 25.4312 13.3457 24.6686 13.6474C23.906 13.9491 23.2517 14.473 22.7905 15.1511C22.3293 15.8292 22.0825 16.6303 22.0821 17.4504C22.0818 17.7641 22.1171 18.0769 22.1875 18.3827C20.5612 18.3012 18.9703 17.8786 17.518 17.1424C16.0657 16.4062 14.7844 15.3729 13.7573 14.1094C13.2343 15.0098 13.0741 16.0757 13.3092 17.0901C13.5443 18.1045 14.1571 18.9912 15.0229 19.5697C14.3733 19.5496 13.738 19.3743 13.1701 19.0584V19.1101C13.1702 20.0544 13.4969 20.9697 14.0949 21.7006C14.6929 22.4315 15.5252 22.933 16.4508 23.1202C16.0997 23.216 15.7372 23.2642 15.3732 23.2636C15.1149 23.2636 14.8571 23.2395 14.6033 23.1916C14.8647 24.0041 15.3734 24.7146 16.0583 25.2238C16.7433 25.733 17.5702 26.0155 18.4235 26.0318C16.7084 27.3748 14.531 27.9836 12.368 27.7249C14.238 28.927 16.4146 29.5653 18.6376 29.5633C20.1687 29.5738 21.6867 29.28 23.1033 28.6989C24.5199 28.1178 25.8069 27.2611 26.8896 26.1784C27.9723 25.0957 28.829 23.8087 29.4101 22.3921C29.9912 20.9755 30.285 19.4575 30.2745 17.9264C30.2745 17.749 30.2703 17.5726 30.262 17.3974C31.0636 16.8176 31.7556 16.0997 32.3055 15.2773Z"
                      fill="white"
                    />
                  </svg>
                  <span className="sr-only">{profile.name} Twitter</span>
                </a>
              </li>
            ) : (
              <li>
                <a
                  href="https://twitter.com/madebystudio"
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    width="43"
                    height="42"
                    viewBox="0 0 43 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <circle cx="21.9684" cy="21" r="21" fill="#00ACEE" />
                    <path
                      d="M32.3055 15.2773C31.559 15.6083 30.7672 15.8255 29.9563 15.9216C30.8105 15.4106 31.4496 14.6064 31.7547 13.6589C30.9519 14.1358 30.0732 14.4715 29.1569 14.6515C28.5955 14.0536 27.8675 13.6382 27.0672 13.4591C26.2669 13.2801 25.4312 13.3457 24.6686 13.6474C23.906 13.9491 23.2517 14.473 22.7905 15.1511C22.3293 15.8292 22.0825 16.6303 22.0821 17.4504C22.0818 17.7641 22.1171 18.0769 22.1875 18.3827C20.5612 18.3012 18.9703 17.8786 17.518 17.1424C16.0657 16.4062 14.7844 15.3729 13.7573 14.1094C13.2343 15.0098 13.0741 16.0757 13.3092 17.0901C13.5443 18.1045 14.1571 18.9912 15.0229 19.5697C14.3733 19.5496 13.738 19.3743 13.1701 19.0584V19.1101C13.1702 20.0544 13.4969 20.9697 14.0949 21.7006C14.6929 22.4315 15.5252 22.933 16.4508 23.1202C16.0997 23.216 15.7372 23.2642 15.3732 23.2636C15.1149 23.2636 14.8571 23.2395 14.6033 23.1916C14.8647 24.0041 15.3734 24.7146 16.0583 25.2238C16.7433 25.733 17.5702 26.0155 18.4235 26.0318C16.7084 27.3748 14.531 27.9836 12.368 27.7249C14.238 28.927 16.4146 29.5653 18.6376 29.5633C20.1687 29.5738 21.6867 29.28 23.1033 28.6989C24.5199 28.1178 25.8069 27.2611 26.8896 26.1784C27.9723 25.0957 28.829 23.8087 29.4101 22.3921C29.9912 20.9755 30.285 19.4575 30.2745 17.9264C30.2745 17.749 30.2703 17.5726 30.262 17.3974C31.0636 16.8176 31.7556 16.0997 32.3055 15.2773Z"
                      fill="white"
                    />
                  </svg>
                  <span className="sr-only">madeby.studio Twitter</span>
                </a>
              </li>
            )}
            {profile.facebook_url && (
              <li>
                <a
                  href={profile.facebook_url}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#3b5998"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm3 8h-1.35c-.538 0-.65.221-.65.778v1.222h2l-.209 2h-1.791v7h-3v-7h-2v-2h2v-2.308c0-1.769.931-2.692 3.029-2.692h1.971v3z" />
                  </svg>
                </a>
                <span className="sr-only">{profile.name} Facebook</span>
              </li>
            )}
            {profile.instagram_url && (
              <li>
                <a
                  href={profile.instagram_url}
                  rel="norefferer"
                  target="_blank"
                  className="duration-default ease-in-out hover:scale-110"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#E1306C"
                    className="h-12 w-12"
                    aria-hidden="true"
                  >
                    <path d="M14.829 6.302c-.738-.034-.96-.04-2.829-.04s-2.09.007-2.828.04c-1.899.087-2.783.986-2.87 2.87-.033.738-.041.959-.041 2.828s.008 2.09.041 2.829c.087 1.879.967 2.783 2.87 2.87.737.033.959.041 2.828.041 1.87 0 2.091-.007 2.829-.041 1.899-.086 2.782-.988 2.87-2.87.033-.738.04-.96.04-2.829s-.007-2.09-.04-2.828c-.088-1.883-.973-2.783-2.87-2.87zm-2.829 9.293c-1.985 0-3.595-1.609-3.595-3.595 0-1.985 1.61-3.594 3.595-3.594s3.595 1.609 3.595 3.594c0 1.985-1.61 3.595-3.595 3.595zm3.737-6.491c-.464 0-.84-.376-.84-.84 0-.464.376-.84.84-.84.464 0 .84.376.84.84 0 .463-.376.84-.84.84zm-1.404 2.896c0 1.289-1.045 2.333-2.333 2.333s-2.333-1.044-2.333-2.333c0-1.289 1.045-2.333 2.333-2.333s2.333 1.044 2.333 2.333zm-2.333-12c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.958 14.886c-.115 2.545-1.532 3.955-4.071 4.072-.747.034-.986.042-2.887.042s-2.139-.008-2.886-.042c-2.544-.117-3.955-1.529-4.072-4.072-.034-.746-.042-.985-.042-2.886 0-1.901.008-2.139.042-2.886.117-2.544 1.529-3.955 4.072-4.071.747-.035.985-.043 2.886-.043s2.14.008 2.887.043c2.545.117 3.957 1.532 4.071 4.071.034.747.042.985.042 2.886 0 1.901-.008 2.14-.042 2.886z" />
                  </svg>
                </a>
              </li>
            )}
          </ul>
          <p>
            Transform <span className="font-bold italic">your</span> digital
            performance.
          </p>
          <AnimatePresence mode="wait" initial={false}>
            {pageView == 'links' && (
              <motion.div
                className="my-12 w-full overflow-hidden"
                variants={animateLinkView}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ul className="flex flex-col items-center space-y-4">
                  <li className="w-full">
                    <ProfileLink
                      title={
                        <p>
                          Visit our website{' '}
                          <span className="ml-1">&#128640;</span>
                        </p>
                      }
                      url="https://madeby.studio"
                    />
                  </li>
                  {profile.calendly_url && (
                    <li className="w-full">
                      <ProfileLink
                        title="Want to grab a coffee? &#9749;"
                        url={profile.calendly_url}
                      />
                    </li>
                  )}
                </ul>
                {categories.map(category => {
                  if (
                    profileLinks.filter(
                      link => link.link_category_id == category.id,
                    ).length > 0
                  ) {
                    return (
                      <div key={category.id} className="mt-8">
                        <p className="my-2 w-full text-center font-medium text-grey">
                          {category.title}
                        </p>
                        <ul className="flex flex-col space-y-4">
                          {profileLinks.map((link, index) => {
                            if (link.link_category_id == category.id) {
                              return (
                                <li key={link.id} className="w-full">
                                  <ProfileLink
                                    title={link.title}
                                    url={link.url}
                                  />
                                </li>
                              );
                            }
                          })}
                        </ul>
                      </div>
                    );
                  }
                })}
                {inviteeEmail == 'undefined' && (
                  <FormSignUp
                    inviteeEmail={inviteeEmailCheck}
                    inviteeName={inviteeFullNameCheck}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence mode="wait" initial={false}>
            {pageView == 'qr' && profile.linkedin_qr_code_url && (
              <motion.div className="my-12 flex items-center justify-center">
                <motion.img
                  src={profile.linkedin_qr_code_url}
                  variants={animateQRView}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex flex-col items-center justify-center">
            <AppTextLogo style="dark" />
            <p className="mt-2">
              We&#39;ve planted {treeCount} trees! &#127795;
            </p>
          </div>
          {pageView == 'links' && profile.linkedin_qr_code_photo_path && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              x="0px"
              y="0px"
              viewBox="0 0 216 146"
              enableBackground="new 0 0 216 146"
              className="text-gray mt-2 h-8 w-8 cursor-pointer fill-current opacity-25 md:hidden"
              onClick={() => handleViewChange('qr')}
            >
              <g>
                <path d="M50.643,130.357h52.143V78.214H50.643V130.357z M61.071,88.643h31.286v31.205H61.071V88.643z"></path>
                <rect x="71.5" y="99.071" width="10.428" height="10.429"></rect>
                <rect
                  x="134.07"
                  y="119.929"
                  width="10.43"
                  height="10.429"
                ></rect>
                <rect
                  x="154.927"
                  y="119.929"
                  width="10.431"
                  height="10.429"
                ></rect>
                <polygon points="154.928,88.643 144.5,88.643 144.5,78.213 113.213,78.213 113.213,130.357 123.643,130.357 123.643,99.071    134.07,99.071 134.07,109.5 165.357,109.5 165.357,78.214 165.357,78.213 154.928,78.213  "></polygon>
                <path d="M50.643,67.787h52.143V15.643H50.643V67.787z M61.071,26.071h31.286v31.286H61.071V26.071z"></path>
                <rect x="71.5" y="36.5" width="10.428" height="10.429"></rect>
                <path d="M113.213,15.643v52.144h52.145V15.643H113.213z M154.928,57.357h-31.285V26.071h31.285V57.357z"></path>
                <rect x="134.07" y="36.5" width="10.43" height="10.429"></rect>
              </g>
            </svg>
          )}
          {pageView == 'qr' && (
            <p
              className="mt-4 text-grey hover:cursor-pointer"
              onClick={() => handleViewChange('links')}
            >
              View my Links
            </p>
          )}
        </div>
      </div>
      <div className="fixed top-0 right-0 hidden h-screen w-6/12 bg-dark 2xl:flex">
        {profile.landing_page_photo_path ? (
          <img
            className="w-full object-cover"
            width="955"
            height="1080"
            src={profile.landing_page_photo_url}
            alt={
              `${profile.name}` - `${profile.user_role}` + ' | madeby.studio'
            }
          />
        ) : (
          <img
            alt="Landing Page Profile Image"
            width="955"
            height="1080"
            className="w-full object-cover"
            src="/assets/landing.jpg"
          />
        )}
      </div>
    </div>
  );
}
